"use client";

import Image from "next/image";
import Link from "next/link";
import { useParams, usePathname, useSearchParams } from "next/navigation";
import { useTranslations } from "next-intl";

type PlaceCardProps = {
  id: number;
  image: string;
  title: string;
  description: string;
  terminales: string[];
  link?: string;
  tipo?: string;
  detalleUbicacion?: string;
};

export default function PlaceCard({
  image,
  title,
  description,
  terminales,
  id,
  link,
  tipo,
  detalleUbicacion,
}: PlaceCardProps) {
  const t = useTranslations("common");
  const { locale } = useParams<{ locale: string }>();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const fromUrl = searchParams.toString()
    ? `${pathname}?${searchParams.toString()}`
    : pathname;

  const detailHref = tipo ? `/${locale}/places/${id}?type=${tipo}` : "#";
  const mapHref = link
    ? `/${locale}/map?src=${encodeURIComponent(link)}&from=${encodeURIComponent(fromUrl)}`
    : `/${locale}/map`;

  const fallbackImage =
    "https://galeria.tocumenaero.com/gallery/api/locales%20generales/321A3961.jpg";

  return (
    <article
      key={id}
      className="group relative flex flex-col bg-white rounded-2xl overflow-hidden border border-gray-200/70 shadow-sm hover:shadow-lg hover:-translate-y-0.5 transition-all duration-300"
    >
      {/* Clickable area → detail page */}
      <Link
        href={detailHref}
        aria-label={t("verMasDe", { nombre: title })}
        className="flex flex-col focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
      >
        {/* Image */}
        <div className="relative aspect-[4/3] overflow-hidden bg-gray-100">
          <Image
            src={image || fallbackImage}
            alt={title}
            fill
            sizes="(min-width: 768px) 33vw, 100vw"
            className="object-cover transition-transform duration-500 ease-out group-hover:scale-105"
          />

          {/* Subtle gradient for badge legibility */}
          <div className="absolute inset-x-0 top-0 h-20 bg-gradient-to-b from-black/40 to-transparent pointer-events-none" />

          {/* Terminal badges (top-left) */}
          <div className="absolute top-3 left-3 flex flex-wrap gap-1.5">
            {terminales
              .filter((t) => t)
              .map((t) => (
                <span
                  key={t}
                  className="inline-flex items-center gap-1.5 bg-white/95 backdrop-blur-sm text-[#102542] text-xs font-semibold px-2.5 py-1 rounded-full shadow-sm"
                >
                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
                  {t}
                </span>
              ))}
          </div>

          {/* Detail affordance (top-right) */}
          <span
            className="absolute top-3 right-3 inline-flex items-center justify-center w-8 h-8 rounded-full bg-white/95 backdrop-blur-sm text-gray-700 shadow-sm group-hover:bg-blue-600 group-hover:text-white transition-colors duration-300"
            aria-hidden="true"
          >
            <svg
              className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2.2}
                d="M7 17L17 7M17 7H9M17 7v8"
              />
            </svg>
          </span>
        </div>

        {/* Content */}
        <div className="flex-1 flex flex-col p-5">
          <h4 className="font-semibold text-lg text-[#102542] leading-tight line-clamp-1 group-hover:text-blue-600 transition-colors">
            {title}
          </h4>

          {detalleUbicacion && (
            <p className="text-xs text-[#102542] mt-1 line-clamp-1 flex items-center gap-1">
              <svg
                className="w-3 h-3 shrink-0"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
                />
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
                />
              </svg>
              {detalleUbicacion}
            </p>
          )}

          {description && (
            <p className="text-sm text-[#102542] mt-3 line-clamp-2 leading-relaxed">
              {description}
            </p>
          )}
        </div>
      </Link>

      {/* Map CTA — separate action */}
      <div className="px-5 pb-5">
        <Link
          href={mapHref}
          className="inline-flex w-full items-center justify-center gap-2 bg-[#102542] hover:bg-[#1a3456] text-white text-sm font-medium px-4 py-2.5 rounded-lg transition-colors duration-200"
        >
          <svg
            className="w-4 h-4"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
            />
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
            />
          </svg>
          {t("verEnMapa")}
        </Link>
      </div>
    </article>
  );
}
